import { lazy, Suspense, useState } from 'react';
import {
  IconArrowBackUp,
  IconArrowForwardUp,
  IconBookmark,
  IconCheck,
  IconDownload,
  IconShare,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { BrandLogo } from '@/components/BrandLogo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { PaletteComposer } from '@/components/studio/PaletteComposer';
import { StudioNavigation } from '@/components/studio/StudioNavigation';
import { PaletteExport } from '@/components/studio/PaletteExport';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useStudioSession } from '@/hooks/use-studio-session';
import type { StudioToolId } from '@/types/studio';

const GradientEditor = lazy(() =>
  import('@/components/studio/GradientEditor').then((m) => ({ default: m.GradientEditor })),
);
const ScaleWorkspace = lazy(() =>
  import('@/components/studio/ScaleWorkspace').then((m) => ({ default: m.ScaleWorkspace })),
);
const ContrastTool = lazy(() =>
  import('@/components/studio/ContrastTool').then((m) => ({ default: m.ContrastTool })),
);
const Playground = lazy(() =>
  import('@/components/studio/Playground').then((m) => ({ default: m.Playground })),
);
const ImageExtractor = lazy(() =>
  import('@/components/studio/ImageExtractor').then((m) => ({ default: m.ImageExtractor })),
);
const PaletteLibrary = lazy(() =>
  import('@/components/studio/PaletteLibrary').then((m) => ({ default: m.PaletteLibrary })),
);

const TOOL_TITLES: Record<StudioToolId, string> = {
  palette: 'Palette Composer',
  gradient: 'Gradient Lab',
  scale: 'Scale Lab',
  contrast: 'Contrast + Mix',
  playground: 'The Playground',
  image: 'Image to palette',
  library: 'Your color library',
};

export default function App() {
  const session = useStudioSession();
  const {
    studio,
    setStudio,
    settings,
    setSettings,
    customPresets,
    presetRegistry,
    notify,
    setActiveTool,
    handleCopy,
    handleTestInContrast,
    handleAddToGradient,
  } = session;
  const [exportOpen, setExportOpen] = useState(false);
  const copy = (text: string, label: string) => {
    void handleCopy(text, label);
  };
  const testPair = (foreground: string, background: string) =>
    setStudio((previous) => ({
      ...previous,
      activeTool: 'contrast',
      contrast: { foreground, background },
    }));

  return (
    <TooltipProvider delay={120}>
      <div className="studio-shell" data-workspace={studio.activeTool}>
        <a className="studio-skip-link" href="#studio-workspace">
          Skip to workspace
        </a>
        <header className="studio-global-header">
          <div className="studio-brand-block">
            <BrandLogo paletteId={settings.accentPalette} onCycle={session.handleCycleAccent} />
            <div>
              <h1>
                Color Studio<span className="studio-brand-dot">.</span>
              </h1>
              <span>A space to play with color</span>
            </div>
          </div>
          <div className="studio-project-name">
            <input
              aria-label="Palette name"
              value={studio.name}
              maxLength={80}
              onChange={(event) =>
                setStudio((previous) => ({ ...previous, name: event.target.value }))
              }
              onBlur={() => {
                if (!studio.name.trim())
                  setStudio((previous) => ({ ...previous, name: 'Untitled palette' }));
              }}
            />
            <span
              className="studio-save-state"
              data-error={session.saveStatus === 'error' || undefined}
              role="status"
            >
              {session.saveStatus === 'saved' ? (
                <>
                  <IconCheck />
                  Saved on this device
                </>
              ) : session.saveStatus === 'saving' ? (
                'Saving…'
              ) : (
                <>
                  <IconAlertTriangle />
                  Not saved
                </>
              )}
            </span>
          </div>
          <div className="studio-global-actions">
            <div className="studio-history-actions">
              <button
                type="button"
                className="studio-icon-button"
                aria-label="Undo edit"
                title="Undo · Ctrl Z"
                aria-keyshortcuts="Control+Z Meta+Z"
                disabled={!session.canUndo}
                onClick={session.undo}
              >
                <IconArrowBackUp />
              </button>
              <button
                type="button"
                className="studio-icon-button"
                aria-label="Redo edit"
                title="Redo · Ctrl Shift Z"
                aria-keyshortcuts="Control+Shift+Z Meta+Shift+Z"
                disabled={!session.canRedo}
                onClick={session.redo}
              >
                <IconArrowForwardUp />
              </button>
            </div>
            <button
              type="button"
              className="studio-icon-button studio-share-action"
              aria-label="Copy palette link"
              title="Copy palette link"
              onClick={session.handleShare}
            >
              <IconShare />
            </button>
            <button
              type="button"
              className="studio-button studio-save-action"
              aria-label="Save palette"
              title="Save palette"
              onClick={session.handleSavePalette}
            >
              <IconBookmark />
              <span>Save palette</span>
            </button>
            <button
              type="button"
              className="studio-button studio-button-primary"
              aria-label="Export palette"
              title="Export palette"
              onClick={() => setExportOpen(true)}
            >
              <IconDownload />
              <span>Export</span>
            </button>
            <ThemeToggle theme={settings.theme} onToggle={session.handleThemeToggle} />
          </div>
        </header>
        {session.saveStatus === 'error' && (
          <div className="studio-storage-warning" role="alert">
            <span>
              Local saving is unavailable. Download a workspace backup from Export before closing.
            </span>
            <button type="button" onClick={session.retrySave}>
              Retry saving
            </button>
          </div>
        )}
        <div className="studio-body">
          <StudioNavigation
            activeTool={studio.activeTool}
            onChange={setActiveTool}
            libraryCount={studio.library.length}
          />
          <main id="studio-workspace" className="studio-workspace" tabIndex={-1}>
            <Suspense
              fallback={
                <div className="studio-tool-loading" role="status">
                  Loading {TOOL_TITLES[studio.activeTool]}…
                </div>
              }
            >
              {studio.activeTool === 'palette' && (
                <PaletteComposer
                  state={studio.palette}
                  onChange={(palette) => setStudio((previous) => ({ ...previous, palette }))}
                  onCopy={copy}
                  onTestInContrast={handleTestInContrast}
                  onAddToGradient={handleAddToGradient}
                  onNotify={notify}
                  onPlayground={session.handlePlayground}
                  onBuildScale={session.handlePaletteScale}
                  onBuildGradient={session.handlePaletteGradient}
                />
              )}
              {studio.activeTool === 'gradient' && (
                <GradientEditor
                  state={studio.gradient}
                  onChange={(gradient) => setStudio((previous) => ({ ...previous, gradient }))}
                  onCopy={copy}
                  onNotify={notify}
                  paletteColors={studio.palette.colors}
                />
              )}
              {studio.activeTool === 'scale' && (
                <ScaleWorkspace
                  settings={settings}
                  setSettings={setSettings}
                  presetRegistry={presetRegistry}
                  customPresetIds={Object.keys(customPresets)}
                  onImportPreset={session.handleImportPreset}
                  onRemovePreset={session.handleRemovePreset}
                  onNotify={notify}
                  onTestInContrast={handleTestInContrast}
                  onUsePalette={session.handleUsePalette}
                />
              )}
              {studio.activeTool === 'contrast' && (
                <ContrastTool
                  contrast={studio.contrast}
                  mixer={studio.mixer}
                  onContrastChange={(contrast) =>
                    setStudio((previous) => ({ ...previous, contrast }))
                  }
                  onMixerChange={(mixer) => setStudio((previous) => ({ ...previous, mixer }))}
                  onCopy={copy}
                  paletteColors={studio.palette.colors}
                />
              )}
              {studio.activeTool === 'playground' && (
                <Playground
                  state={studio.playground}
                  colors={studio.palette.colors}
                  onChange={session.setPlayground}
                  onCopy={copy}
                  onTestPair={testPair}
                  onNotify={notify}
                />
              )}
              {studio.activeTool === 'image' && (
                <ImageExtractor onUse={session.handleUsePalette} onCopy={copy} onNotify={notify} />
              )}
              {studio.activeTool === 'library' && (
                <PaletteLibrary
                  library={studio.library}
                  onUse={session.handleUsePalette}
                  onSaveCurrent={session.handleSavePalette}
                  onUpdate={session.handleUpdateSaved}
                  onRemove={session.handleRemoveSaved}
                  onImport={session.handleImportPalettes}
                  onRestore={session.handleRestoreProject}
                  onNotify={notify}
                />
              )}
            </Suspense>
          </main>
        </div>
        <PaletteExport
          open={exportOpen}
          onOpenChange={setExportOpen}
          colors={studio.palette.colors}
          name={studio.name}
          document={session.currentDocument}
          onCopy={copy}
          onNotify={notify}
        />
      </div>
    </TooltipProvider>
  );
}
